import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const ACTIVITY_UPDATE_INTERVAL = 60000; // Update every 60 seconds
const ONLINE_THRESHOLD_MINUTES = 5;

export const useUserActivity = (userId: string | undefined, userType: string = 'internal') => {
  const lastUpdateRef = useRef<number>(0);

  const updateActivity = useCallback(async () => {
    if (!userId) return;

    const now = Date.now();
    // Debounce: Only update if 30+ seconds since last update
    if (now - lastUpdateRef.current < 30000) return;
    
    lastUpdateRef.current = now;

    try {
      const { error } = await supabase
        .from('user_activity')
        .upsert({
          user_id: userId,
          user_type: userType,
          last_active_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,user_type'
        });

      if (error) {
        console.error('Error updating user activity:', error);
      }
    } catch (err) {
      console.error('Failed to update activity:', err);
    }
  }, [userId, userType]);

  useEffect(() => {
    if (!userId) return;

    // Update activity on mount
    updateActivity();

    // Update activity periodically
    const interval = setInterval(updateActivity, ACTIVITY_UPDATE_INTERVAL);

    // Update activity on user interaction
    const handleActivity = () => updateActivity();
    
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, [userId, updateActivity]);

  return { updateActivity };
};

export const isUserOnline = (lastActiveAt: string | null): boolean => {
  if (!lastActiveAt) return false;
  
  const lastActive = new Date(lastActiveAt);
  const now = new Date();
  const diffMinutes = (now.getTime() - lastActive.getTime()) / (1000 * 60);
  
  return diffMinutes <= ONLINE_THRESHOLD_MINUTES;
};

export const getLastSeenText = (lastActiveAt: string | null): string => {
  if (!lastActiveAt) return 'Never';
  
  const lastActive = new Date(lastActiveAt);
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60));
  
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};
